import React from 'react';
import { toast } from 'react-toastify';
import ColorPicker from '../ColorPicker';
import {
    giveTopPostsLuv,
    listMyGivenEngagementsRecent,
    getProfileEngagementSummary,
} from '../../services/api';
import EditableBox from './EditableBox';
import { useProfileSkins, isSkinSaveable } from '../../data/profileSkins';

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_LUV_HEART_COLOUR = "#e72354";
const LUV_DAILY_MAX = 3;
const GENERIC_CACHE_TTL_MS = 30 * 60 * 1000;

const _assetMatchesHeart = (a) => {
    const p = a.placement;
    if (Array.isArray(p)) return p.includes('heart');
    return typeof p === 'string' && (p === 'heart' || p.includes('heart'));
};

const getHeartAssets = (skin) =>
    skin && skin.id !== 'default' ? (skin.assets || []).filter(_assetMatchesHeart) : [];

// ── Utilities (local copies of shared Profile helpers) ────────────────────────

const getCachedData = (key) => {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.ts > GENERIC_CACHE_TTL_MS) return null;
        return parsed.data;
    } catch {
        return null;
    }
};

const setCachedData = (key, data) => {
    try {
        sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch {}
};

const clearCachedData = (key) => {
    try { sessionStorage.removeItem(key); } catch {}
};

const hexToRgb = (hex) => {
    const h = String(hex || '').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return { r: 255, g: 255, b: 255 };
    return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
};

// ── Luv localStorage helpers ──────────────────────────────────────────────────

const getLuvDateKey = () => new Date().toISOString().slice(0, 10);

const getLuvStorageKey = (fromUser) =>
    `luv_given_${String(fromUser || '').toLowerCase()}_${getLuvDateKey()}`;

const getLuvGivenToday = (fromUser) => {
    try {
        const raw = localStorage.getItem(getLuvStorageKey(fromUser));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
};

const recordLuvGiven = (fromUser) => {
    try {
        const existing = getLuvGivenToday(fromUser);
        const next = [...existing, Date.now()];
        localStorage.setItem(getLuvStorageKey(fromUser), JSON.stringify(next));
        try {
            const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
            localStorage.removeItem(`luv_given_${String(fromUser || '').toLowerCase()}_${yesterday}`);
        } catch {}
    } catch {}
};

const reconcileLuvCache = (fromUser, serverUsedCount) => {
    try {
        const existing = getLuvGivenToday(fromUser);
        const deficit = serverUsedCount - existing.length;
        if (deficit <= 0) return existing;
        const synthetic = Array.from({ length: deficit }, () => Date.now());
        const next = [...existing, ...synthetic];
        localStorage.setItem(getLuvStorageKey(fromUser), JSON.stringify(next));
        return next;
    } catch { return getLuvGivenToday(fromUser); }
};

const isLuvToday = (createdAt) => {
    try {
        return new Date(createdAt).toISOString().slice(0, 10) === getLuvDateKey();
    } catch { return false; }
};

// ── Component ─────────────────────────────────────────────────────────────────

const LuvBox = ({
                    profileUsername,
                    isOwnProfile,
                    isLoggedIn,
                    isGuest,
                    currentUsername,
                    currentUserId,
                    isEditing,
                    isOwnProfileBool,
                    getHeaderColour,
                    setHeaderColour,
                    restrictLiking,
                    restrictionExpiry,
                    dragEnabled,
                    dragHandleProps,
                    editingHeader,
                    setEditingHeader,
                    headerTitle,
                    setHeaderTitle,
                    luvHeartColour,
                    setLuvHeartColour,
                    getBoxSkin,
                    setBoxSkin,
                    isSteve,
                    isStaff,
                    ownedSkinIds,
                    luvHeartSkin,
                    setLuvHeartSkin,
                    // Pre-seeded from the page-load aggregate — skips initial API calls
                    initialLuvTotal,
                    initialLuvRecentGiven,
                }) => {

    const tintId = React.useMemo(
        () => `luvTint_${Math.random().toString(36).slice(2)}`,
        []
    );

    const allSkins = useProfileSkins();
    const HEART_SKINS = React.useMemo(
        () => allSkins.filter(s => !s.staffOnly || isStaff),
        [allSkins, isStaff]
    );

    const activeLuvHeartSkinId = luvHeartSkin || 'default';
    const activeLuvHeartSkin = React.useMemo(
        () => HEART_SKINS.find(s => s.id === activeLuvHeartSkinId) ?? null,
        [HEART_SKINS, activeLuvHeartSkinId]
    );
    const heartAssets = React.useMemo(() => getHeartAssets(activeLuvHeartSkin), [activeLuvHeartSkin]);
    const heartBehindLayers  = heartAssets.filter(a => a.heartDepth === 'behind');
    const heartInfrontLayers = heartAssets.filter(a => a.heartDepth !== 'behind');

    const [isHeartSkinPickerOpen, setIsHeartSkinPickerOpen] = React.useState(false);

    React.useEffect(() => {
        if (!(isEditing && isOwnProfile)) setIsHeartSkinPickerOpen(false);
    }, [isEditing, isOwnProfile]);

    const toggleHeartSkinPicker = (e) => {
        e.stopPropagation();
        setIsHeartSkinPickerOpen(v => !v);
    };

    const { r, g, b } = hexToRgb(luvHeartColour || DEFAULT_LUV_HEART_COLOUR);
    const mr = (r / 255).toFixed(6);
    const mg = (g / 255).toFixed(6);
    const mb = (b / 255).toFixed(6);

    const darkenHex = (hex, amount = 0.25) => {
        const { r, g, b } = hexToRgb(hex);
        const dr = Math.max(0, Math.floor(r * (1 - amount)));
        const dg = Math.max(0, Math.floor(g * (1 - amount)));
        const db = Math.max(0, Math.floor(b * (1 - amount)));
        return `#${[dr, dg, db].map(v => v.toString(16).padStart(2, "0")).join("")}`;
    };

    const baseColour = luvHeartColour || DEFAULT_LUV_HEART_COLOUR;
    const darkerColour = darkenHex(baseColour, 0.35);

    const [luvCount, setLuvCount] = React.useState(null);
    const [luvLoading, setLuvLoading] = React.useState(true);
    const [giving, setGiving] = React.useState(false);
    const [givenToday, setGivenToday] = React.useState(() =>
        isLoggedIn && currentUsername ? getLuvGivenToday(currentUsername) : []
    );
    const [floatingHearts, setFloatingHearts] = React.useState([]);
    const [cooldownEnd, setCooldownEnd] = React.useState(0);
    const [secsLeft, setSecsLeft] = React.useState(0);

    React.useEffect(() => {
        if (cooldownEnd <= Date.now()) return;
        setSecsLeft(Math.ceil((cooldownEnd - Date.now()) / 1000));
        const id = setInterval(() => {
            const left = Math.ceil(Math.max(0, (cooldownEnd - Date.now()) / 1000));
            setSecsLeft(left);
            if (left <= 0) clearInterval(id);
        }, 250);
        return () => clearInterval(id);
    }, [cooldownEnd]);

    const onCooldown = secsLeft > 0;

    const remaining = isSteve ? Number.POSITIVE_INFINITY : Math.max(0, LUV_DAILY_MAX - givenToday.length);

    React.useEffect(() => {
        let cancelled = false;
        setLuvLoading(true);
        setLuvCount(null);

        const load = async () => {
            try {
                // Use pre-seeded data from the page-load aggregate when available,
                // falling back to individual API calls for refreshes or own-profile.
                const luvCacheKey = `luv_summary_${String(profileUsername || '').toLowerCase()}`;
                let total;
                if (initialLuvTotal != null && Number.isFinite(Number(initialLuvTotal)) && !isOwnProfile) {
                    // Aggregate pre-seeded this value — use it directly
                    total = Number(initialLuvTotal);
                } else if (!isOwnProfile) {
                    const cached = getCachedData(luvCacheKey);
                    if (cached !== null && Number.isFinite(Number(cached))) {
                        total = Number(cached);
                    } else {
                        const sum = await getProfileEngagementSummary(profileUsername, 'luv', 'profile');
                        total = Number(sum?.total || 0);
                        setCachedData(luvCacheKey, total);
                    }
                } else {
                    const sum = await getProfileEngagementSummary(profileUsername, 'luv', 'profile');
                    total = Number(sum?.total || 0);
                }
                if (!cancelled) setLuvCount(total);

                if (isLoggedIn && currentUsername) {
                    // Use pre-seeded recent-given data from aggregate when available
                    const recent = Array.isArray(initialLuvRecentGiven) && initialLuvRecentGiven.length >= 0
                        ? initialLuvRecentGiven
                        : (() => {
                            // fallback: fetch fresh
                            return null;
                        })();
                    if (recent !== null) {
                        const usedToday = recent.filter(e => isLuvToday(e?.createdAt)).length;
                        const reconciled = reconcileLuvCache(currentUsername, usedToday);
                        if (!cancelled) setGivenToday(reconciled);
                    } else {
                        const recentRes = await listMyGivenEngagementsRecent('luv', 'profile', 3);
                        const fetched = Array.isArray(recentRes?.engagements) ? recentRes.engagements : [];
                        const usedToday = fetched.filter(e => isLuvToday(e?.createdAt)).length;
                        const reconciled = reconcileLuvCache(currentUsername, usedToday);
                        if (!cancelled) setGivenToday(reconciled);
                    }
                }
            } catch {
                if (!cancelled) setLuvCount(0);
            } finally {
                if (!cancelled) setLuvLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [profileUsername, isLoggedIn, currentUsername, currentUserId]);

    React.useEffect(() => {
        if (!isLoggedIn || !currentUsername) return;

        const schedule = () => {
            const now = new Date();
            const nextMidnight = new Date(Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate() + 1,
                0, 0, 1
            ));
            const ms = nextMidnight.getTime() - Date.now();

            return setTimeout(() => {
                setGivenToday(getLuvGivenToday(currentUsername));
                schedule();
            }, Math.max(1000, ms));
        };

        const t = schedule();
        return () => clearTimeout(t);
    }, [isLoggedIn, currentUsername]);

    React.useEffect(() => {
        if (isLoggedIn && currentUsername) {
            setGivenToday(getLuvGivenToday(currentUsername));
        }
    }, [currentUsername, isLoggedIn]);

    const spawnHearts = () => {
        const now = Date.now();
        const burst = Array.from({ length: 6 }, (_, i) => ({
            id: now + i + Math.random(),
            x: 10 + Math.random() * 80,
            size: 1.2 + Math.random() * 1.4,
            dur: 900 + Math.random() * 500,
            delay: i * 60,
        }));
        setFloatingHearts(prev => [...prev, ...burst]);
        burst.forEach(h => {
            setTimeout(() => {
                setFloatingHearts(prev => prev.filter(fh => fh.id !== h.id));
            }, h.dur + h.delay + 100);
        });
    };

    const handleGiveLuv = async () => {
        if (!isLoggedIn) { toast.warn('You must be logged in to give Luv!'); return; }
        if (!isSteve && isOwnProfile) { toast.warn("You can't give Luv to yourself!"); return; }
        if (!isSteve && remaining <= 0) { toast.warn("You've used all your Luv for today! Come back tomorrow!"); return; }
        if (giving || onCooldown) return;
        setCooldownEnd(Date.now() + 3000);

        if (restrictLiking) {
            const expiry = restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : '';
            toast.warn(`You are restricted from giving Luv${expiry}.`);
            return;
        }

        setGiving(true);
        spawnHearts();

        try {
            const res = await giveTopPostsLuv(profileUsername);

            if (res?.success === false) {
                const serverUsed = Number(res?.usedToday ?? res?.used_today ?? NaN);
                if (Number.isFinite(serverUsed) && serverUsed > givenToday.length) {
                    const reconciled = reconcileLuvCache(currentUsername, serverUsed);
                    setGivenToday(reconciled);
                    const realRemaining = Math.max(0, LUV_DAILY_MAX - reconciled.length);
                    if (realRemaining === 0) {
                        toast.warn("You've used all your Luv for today! Come back tomorrow!");
                    } else {
                        toast.warn(`You only have ${realRemaining} Luv left today.`);
                    }
                } else {
                    toast.error('Failed to send Luv. Please try again!');
                }
                return;
            }

            recordLuvGiven(currentUsername);
            const updated = getLuvGivenToday(currentUsername);

            const confirmedUsed = Number(res?.usedToday ?? res?.used_today ?? NaN);
            const finalUsed = Number.isFinite(confirmedUsed)
                ? reconcileLuvCache(currentUsername, confirmedUsed)
                : updated;

            setGivenToday(finalUsed);
            setLuvCount(prev => {
                const next = (prev ?? 0) + 1;
                // Bust the cache so the updated count is stored
                clearCachedData(`luv_summary_${String(profileUsername || '').toLowerCase()}`);
                return next;
            });

            const newRemaining = Math.max(0, LUV_DAILY_MAX - finalUsed.length);
            if (newRemaining === 0) {
                toast.success('Luv sent! That was your last one for today!');
            } else {
                toast.success(`Luv sent! You have ${newRemaining} Luv left today.`);
            }


        } catch {
            toast.error('Failed to send Luv. Please try again!');
        } finally {
            setGiving(false);
        }
    };

    const mergedHeaderProps = {
        isEditing,
        isOwnProfile: isOwnProfileBool,
        getHeaderColour,
        setHeaderColour,
        ...(getBoxSkin ? { getBoxSkin } : {}),
        ...(typeof setBoxSkin === 'function' ? { setBoxSkin } : {}),
        ...(editingHeader !== undefined ? { editingHeader } : {}),
        ...(typeof setEditingHeader === 'function' ? { setEditingHeader } : {}),
        ...(dragEnabled !== undefined ? { dragEnabled } : {}),
        ...(dragHandleProps ? { dragHandleProps } : {}),
        ...(headerTitle !== undefined ? { headerTitle } : {}),
        ...(typeof setHeaderTitle === 'function' ? { setHeaderTitle } : {}),
        ...(isStaff !== undefined ? { isStaff } : {}),
        ...(ownedSkinIds !== undefined ? { ownedSkinIds } : {}),
    };

    return (
        <>
        <EditableBox
            boxKey="luv"
            header="❤️ Luv"
            padding="p-0"
            {...mergedHeaderProps}
        >
            <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
                <filter id={tintId} colorInterpolationFilters="sRGB">
                    <feColorMatrix
                        type="matrix"
                        values={`${mr} 0 0 0 0  0 ${mg} 0 0 0  0 0 ${mb} 0 0  0 0 0 1 0`}
                    />
                </filter>
            </svg>
            <div className="relative select-none">
                {/* Floating hearts burst layer */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
                    {floatingHearts.map(h => (
                        <div
                            key={h.id}
                            className="absolute bottom-2"
                            style={{
                                left: `${h.x}%`,
                                animation: `luvHeartFloat ${h.dur}ms ease-out ${h.delay}ms both`
                            }}
                        >
                            <span style={{ position: "relative", display: "inline-block" }}>
                                {heartBehindLayers.map((asset, i) => (
                                    <img
                                        key={i}
                                        src={asset.image}
                                        alt=""
                                        draggable={false}
                                        style={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%) scale(0.5)",
                                            pointerEvents: "none",
                                            imageRendering: "pixelated",
                                            maxWidth: "none",
                                        }}
                                    />
                                ))}
                                <img
                                    src="/img/luv_heart_lg.png"
                                    alt=""
                                    draggable={false}
                                    style={{
                                        display: "block",
                                        filter: `url(#${tintId})`,
                                        imageRendering: "pixelated"
                                    }}
                                />
                                {heartInfrontLayers.length > 0 ? (
                                    heartInfrontLayers.map((asset, i) => (
                                        <img
                                            key={i}
                                            src={asset.image}
                                            alt=""
                                            draggable={false}
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%) scale(0.5)",
                                                pointerEvents: "none",
                                                imageRendering: "pixelated",
                                                maxWidth: "none",
                                            }}
                                        />
                                    ))
                                ) : (
                                    <img
                                        src="/img/luv_heart_lg_sparkle.png"
                                        alt=""
                                        draggable={false}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            pointerEvents: "none",
                                            imageRendering: "pixelated"
                                        }}
                                    />
                                )}
                            </span>
                        </div>
                    ))}
                </div>

                <style>{`
                    @keyframes luvHeartFloat {
                        0%   { transform: translateY(0) scale(1);       opacity: 1; }
                        60%  { transform: translateY(-60px) scale(1.2);  opacity: 0.85; }
                        100% { transform: translateY(-100px) scale(0.6); opacity: 0; }
                    }
                `}</style>

                <div className="flex items-center justify-center gap-3 pt-4 pb-2 px-4">
                    <div className="flex flex-col items-center gap-0.5">
                        <div className="font-['Jersey_10'] text-5xl leading-none text-coal tabular-nums">
                            {luvLoading ? (
                                <span className="text-coal/40 text-3xl">...</span>
                            ) : (
                                (luvCount ?? 0).toLocaleString()
                            )}
                        </div>
                        <div className="font-['Jersey_10'] text-lg text-coal/60 uppercase tracking-widest leading-none">
                            Total Luv
                        </div>
                    </div>
                    <div
                        className="ml-2 relative w-[60px] h-[60px] flex justify-center items-center"
                    >
                        {heartBehindLayers.map((asset, i) => (
                            <img
                                key={i}
                                src={asset.image}
                                alt=""
                                draggable={false}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    imageRendering: "pixelated",
                                    maxWidth: "none",
                                    zIndex: 1,
                                    pointerEvents: "none",
                                }}
                            />
                        ))}

                        <img
                            src="/img/luv_heart_lg.png"
                            alt=""
                            draggable={false}
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%) scale(2)",
                                display: "block",
                                filter: `url(#${tintId}) drop-shadow(0 2px 6px rgba(0,0,0,0.25))`,
                                imageRendering: "pixelated",
                                zIndex: 2,
                            }}
                        />

                        {heartInfrontLayers.length > 0 ? (
                            heartInfrontLayers.map((asset, i) => (
                                <img
                                    key={i}
                                    src={asset.image}
                                    alt=""
                                    draggable={false}
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        imageRendering: "pixelated",
                                        maxWidth: "none",
                                        zIndex: 3,
                                        pointerEvents: "none",
                                    }}
                                />
                            ))
                        ) : (
                            <img
                                src="/img/luv_heart_lg_sparkle.png"
                                alt=""
                                draggable={false}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%) scale(2)",
                                    pointerEvents: "none",
                                    imageRendering: "pixelated",
                                    zIndex: 3,
                                }}
                            />
                        )}
                    </div>

                    {isEditing && isOwnProfile && (
                        <div className="flex flex-col gap-1 items-center">
                            <ColorPicker
                                value={luvHeartColour || DEFAULT_LUV_HEART_COLOUR}
                                onChange={(e) => setLuvHeartColour(e.target.value)}
                                className="h-6 w-6 p-0 border border-black/50 rounded cursor-pointer"
                                title="Heart colour"
                            />
                            {typeof setLuvHeartSkin === 'function' && HEART_SKINS.length > 1 && (
                                <button
                                    type="button"
                                    onClick={toggleHeartSkinPicker}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="h-6 w-6 border border-black/50 overflow-hidden cursor-pointer relative shrink-0 flex items-center justify-center bg-white/20"
                                    title="Heart skin"
                                >
                                    <img
                                        src="/img/luv_heart_lg.png"
                                        alt=""
                                        draggable={false}
                                        style={{ imageRendering: "pixelated", pointerEvents: "none" }}
                                    />
                                    {activeLuvHeartSkin && heartInfrontLayers[0] && (
                                        <img
                                            src={heartInfrontLayers[0].image}
                                            alt=""
                                            draggable={false}
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)",
                                                imageRendering: "pixelated",
                                                maxWidth: "none",
                                                pointerEvents: "none",
                                            }}
                                        />
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="mx-4 border-t border-black/10 my-1" />

                <div className="px-4 pb-4 pt-2 flex flex-col items-center gap-2">
                    {isOwnProfile && !isSteve ? (
                        <div className="text-center font-['Jersey_10'] text-lg text-coal/50 py-1">
                            This is your page — no self-Luv!
                        </div>
                    ) : (isGuest || !isLoggedIn) ? (
                        <div className="text-center font-['Jersey_10'] text-lg text-coal/50 py-1">
                            Log in to give Luv!
                        </div>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleGiveLuv}
                                disabled={giving || onCooldown || remaining === 0}
                                className={`
                                    w-full py-3 px-4 border-2 border-black font-['Jersey_10'] text-2xl uppercase tracking-wide
                                    transition-opacity duration-150 relative overflow-hidden
                                    ${remaining === 0
                                    ? 'bg-black/10 text-coal/40 cursor-not-allowed border-black/20'
                                    : (giving || onCooldown)
                                        ? 'text-white opacity-60 cursor-wait'
                                        : 'text-white active:scale-95 cursor-pointer'
                                }
                                `}
                                style={remaining > 0 ? {
                                    background: `linear-gradient(to bottom right, ${baseColour}, ${darkerColour})`,
                                    boxShadow: '0 4px 0 0 rgba(0,0,0,0.3)',
                                    textShadow: `
                                        -1px -1px 0 #000,
                                         1px -1px 0 #000,
                                        -1px  1px 0 #000,
                                         1px  1px 0 #000
                                    `
                                } : {}}
                            >
                                {remaining === 0
                                    ? '💔 No Luv Left Today'
                                    : onCooldown
                                        ? `Wait ${secsLeft}s...`
                                        : `Give Luv (${remaining})`
                                }
                            </button>

                            <div className="flex items-center gap-2">
                                {Array.from({ length: LUV_DAILY_MAX }).map((_, i) => (
                                    <div
                                        key={i}
                                        className={`w-3 h-3 rounded-full border border-black/30 transition-colors duration-300 ${
                                            i < (LUV_DAILY_MAX - remaining)
                                                ? 'bg-rose-400'
                                                : 'bg-black/10'
                                        }`}
                                        title={i < (LUV_DAILY_MAX - remaining) ? 'Used' : 'Available'}
                                    />
                                ))}
                                <span className="font-['Jersey_10'] text-sm text-coal/50 ml-1">
                                    {remaining}/{LUV_DAILY_MAX} today
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {isHeartSkinPickerOpen && (
                    <div
                        className="absolute inset-0 z-[210] flex flex-col bg-offwhite border-2 border-coal shadow-[0_3px_0_0_rgba(0,0,0,0.5)]"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between border-b border-gray-300 shrink-0">
                            <p className="px-3 py-2 text-md leading-snug text-coal font-['Jersey_10'] uppercase tracking-widest">
                                Pick a skin for your heart. Don't own one? You can still preview it on your profile, but you can't save it.
                            </p>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setIsHeartSkinPickerOpen(false); }}
                                className="h-7 w-7 m-1 flex items-center justify-center hover:bg-black/10 rounded shrink-0 text-coal"
                                title="Close"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto habbo-scroll-v" style={{ containerType: 'inline-size' }}>
                        <style>{`
                            .skin-picker-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                            @container (min-width: 420px) { .skin-picker-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
                            @container (min-width: 520px) { .skin-picker-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
                            @container (min-width: 620px) { .skin-picker-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); } }
                        `}</style>
                        <div className="skin-picker-grid p-2 grid content-start gap-2">
                            {HEART_SKINS.map(skin => {
                                const skinHeartAssets = getHeartAssets(skin);
                                const infrontPreviews = skinHeartAssets.filter(a => a.heartDepth !== 'behind');
                                const saveable = isSkinSaveable(skin.id, isStaff, ownedSkinIds || []);
                                const isActive = activeLuvHeartSkinId === skin.id;
                                return (
                                    <button
                                        key={skin.id}
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); if (typeof setLuvHeartSkin === 'function') setLuvHeartSkin(skin.id); setIsHeartSkinPickerOpen(false); }}
                                        className={`flex flex-col items-center gap-1 p-1 w-full min-w-0 hover:bg-black/5 transition-colors ${isActive ? 'outline outline-2 outline-coal' : ''}`}
                                        title={saveable ? skin.name : `${skin.name} (preview – you don't own this skin yet)`}
                                    >
                                        <div className="w-10 h-10 border border-gray-300 overflow-hidden relative shrink-0 flex items-center justify-center bg-gray-100">
                                            <img
                                                src="/img/luv_heart_lg.png"
                                                alt=""
                                                draggable={false}
                                                style={{ imageRendering: "pixelated", transform: "scale(2)", pointerEvents: "none" }}
                                            />
                                            {infrontPreviews[0] && (
                                                <img
                                                    src={infrontPreviews[0].image}
                                                    alt=""
                                                    draggable={false}
                                                    style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        imageRendering: "pixelated",
                                                        maxWidth: "none",
                                                        pointerEvents: "none",
                                                    }}
                                                />
                                            )}
                                            {!saveable && (
                                                <div style={{
                                                    position: 'absolute', inset: 0,
                                                    backgroundColor: 'rgba(255,255,255,0.45)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    overflow: 'hidden', pointerEvents: 'none',
                                                }}>
                                                    <span style={{
                                                        transform: 'rotate(-35deg)',
                                                        fontSize: '14px',
                                                        fontFamily: "'Jersey 10', sans-serif",
                                                        fontWeight: 900,
                                                        letterSpacing: '0.02em',
                                                        color: 'rgba(0,0,0,0.82)',
                                                        textTransform: 'uppercase',
                                                        whiteSpace: 'nowrap',
                                                        lineHeight: 1,
                                                        userSelect: 'none',
                                                    }}>PREVIEW</span>
                                                </div>
                                            )}
                                        </div>
                                        <span className="w-full truncate text-center text-sm text-coal font-['Jersey_10'] uppercase leading-none tracking-widest">{skin.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                        </div>
                    </div>
                )}
            </div>
        </EditableBox>
        </>
    );
};

export default LuvBox;